<template>
  <div class="tinto-page">
    <Loading :isActive="loading" />

    <TintometricoVincularEanModal
      name="baseForm"
      context-title="Base em edição"
      :context-fields="baseContextFields"
      :ean-atual="editForm.ean"
      :endpoint="`/admin/tintometrico/base/${editForm.id}`"
      @saved="load"
    />

    <!-- ===== FILTERS ===== -->
    <div class="tinto-filters">
      <div class="tinto-filtros__linha">
        <div class="tinto-filtros__busca">
          <input class="form-control" v-model="filtros.termo" placeholder="Busca rápida: nome, código ou EAN..." @keyup.enter="load()" />
        </div>
        <div class="tinto-filtros__acoes">
          <button class="button button-primary button--icone" @click="load()" title="Buscar">
            <fa-icon icon="search" />
          </button>
          <button
            :class="['button', 'button--auto', filtrosAbertos ? 'button-primary' : '']"
            @click="filtrosAbertos = !filtrosAbertos"
          >
            <fa-icon icon="filter" /> Filtros
            <span v-if="totalFiltrosAtivos" class="tinto-filtros__contador">{{ totalFiltrosAtivos }}</span>
          </button>
        </div>
      </div>

      <div class="tinto-filtros__painel" v-if="filtrosAbertos">
        <CRow>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Fabricante</label>
              <select class="form-control" v-model="filtros.fabricanteId" @change="onFabricanteChange()">
                <option value="">Todos</option>
                <option v-for="(n,id) in fabricantes" :key="id" :value="id">{{ n }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Origem</label>
              <select class="form-control" v-model="filtros.integracaoId" @change="load()">
                <option value="">Todas</option>
                <option v-for="i in integracoesDoFabricante" :key="i.id" :value="i.id">{{ i.descricao }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Linha</label>
              <select class="form-control" v-model="filtros.linhaId" @change="load()">
                <option value="">Todas</option>
                <option v-for="o in linhasDisponiveis" :key="o.id" :value="o.id">{{ o.nome }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Embalagem</label>
              <select class="form-control" v-model="filtros.embalagemId" @change="load()">
                <option value="">Todas</option>
                <option v-for="o in embalagensDisponiveis" :key="o.id" :value="o.id">{{ o.nome }}</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Vínculo com produto</label>
              <select class="form-control" v-model="filtros.eanPreenchido" @change="load()">
                <option value="">Todos</option>
                <option value="1">Com produto</option>
                <option value="0">Sem produto</option>
              </select>
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Nome</label>
              <input class="form-control" v-model="filtros.nome" placeholder="contém..." @keyup.enter="load()" />
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">Código</label>
              <input class="form-control" v-model="filtros.codigo" placeholder="A2, P..." @keyup.enter="load()" />
            </div>
          </CCol>
          <CCol md="3">
            <div class="tinto-campo">
              <label class="tinto-campo__label">EAN</label>
              <input class="form-control" v-model="filtros.ean" placeholder="contém..." @keyup.enter="load()" />
            </div>
          </CCol>
        </CRow>
      </div>

      <div class="tinto-chips" v-if="chipsFiltros.length">
        <span class="tinto-chips__titulo">Filtrando por</span>
        <span class="tinto-filtro-chip" v-for="chip in chipsFiltros" :key="chip.chave">
          <strong>{{ chip.rotulo }}:</strong> {{ chip.valor }}
          <span class="tinto-filtro-chip__remover" @click="removerFiltro(chip.chave)" title="Remover filtro">&times;</span>
        </span>
        <button class="tinto-chips__limpar" @click="limparFiltros()">limpar tudo</button>
      </div>
    </div>

    <!-- ===== TABLE ===== -->
    <div class="tinto-table-wrap">
      <table class="tinto-table">
        <thead><tr>
          <th style="width:50px">ID</th>
          <th style="width:280px" class="tinto-sortable" :class="{'is-active': sort.by==='nome'}" @click="sortBy('nome')">
            Linha<fa-icon :icon="sort.by==='nome' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:90px" class="tinto-sortable" :class="{'is-active': sort.by==='codigo'}" @click="sortBy('codigo')">
            Código<fa-icon :icon="sort.by==='codigo' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:140px">Embalagem</th>
          <th style="width:130px">EAN</th>
          <th style="width:110px">Fabricante</th>
          <th style="width:130px">Origem</th>
          <th style="width:50px"></th>
        </tr></thead>
        <tbody>
          <tr v-if="items.length === 0"><td colspan="8" class="tinto-table__empty">Nenhuma base encontrada</td></tr>
          <tr v-for="r in items" :key="r.id">
            <td>{{ r.id }}</td>
            <td class="tinto-table__primary">
              <span v-if="r.linhaNome">{{ r.linhaNome }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>{{ r.codigo }}</td>
            <td>
              <span v-if="r.embalagemNome">{{ r.embalagemNome }}</span>
              <span v-if="r.embalagemVolumeMl" class="tinto-table__sub" style="display:inline;margin-left:4px">({{ formatVolume(r.embalagemVolumeMl) }})</span>
              <span v-if="!r.embalagemNome" class="tinto-table__muted">—</span>
            </td>
            <td><span v-if="r.ean">{{ r.ean }}</span><span v-else class="tinto-table__muted">—</span></td>
            <td>{{ r.nomeFabricante }}</td>
            <td>
              <span v-if="r.integracaoNome">{{ r.integracaoNome }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>
              <button class="button button-primary" @click="openEdit(r)" title="Gerenciar EAN">
                <fa-icon icon="pencil-alt" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="tinto-pagination" v-if="paginator.last_page > 1">
      <pagination :data="paginator" :limit="5" @pagination-change-page="load" />
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import TintometricoVincularEanModal from "./TintometricoVincularEanModal.vue";
import listagemMixin from "./listagemMixin";

export default {
  name: "tintometrico-base",
  components: { Loading, TintometricoVincularEanModal },
  mixins: [listagemMixin],
  props: ['fabricantes', 'integracoes', 'embalagens', 'linhas'],

  data() {
    return {
      listagemEndpoint: '/admin/tintometrico/base',
      sort: { by: 'codigo', dir: 'asc' },
      filtros: { termo: '', fabricanteId: '', integracaoId: '', linhaId: '', embalagemId: '', nome: '', codigo: '', ean: '', eanPreenchido: '' },
      rotulosFiltros: {
        termo: 'Busca', fabricanteId: 'Fabricante', integracaoId: 'Origem', linhaId: 'Linha',
        embalagemId: 'Embalagem', eanPreenchido: 'Vínculo', nome: 'Nome', codigo: 'Código', ean: 'EAN',
      },
      editForm: { id: null, codigo: '', linhaNome: '', embalagemNome: '', embalagemVolumeMl: null, nomeFabricante: '', ean: '' },
    };
  },

  computed: {
    // Origens disponíveis para escolher: quando um fabricante está selecionado,
    // só as integrações dele. O catálogo tem uma cópia por integração quando o
    // fabricante tem mais de uma fonte (duas máquinas, por exemplo).
    // Linha e embalagem existem uma vez POR INTEGRAÇÃO quando o fabricante tem
    // mais de uma fonte. O select segue o recorte já escolhido na tela para não
    // listar a mesma embalagem repetida nem oferecer a da outra máquina.
    // Linha e embalagem existem uma vez POR INTEGRAÇÃO quando o fabricante tem
    // mais de uma máquina. O select mostra UMA opção por identidade (o nome que
    // o usuário conhece) e leva junto todos os ids daquela identidade, senão a
    // lista sairia duplicada e escolher uma opção esconderia as bases da outra
    // máquina.
    linhasDisponiveis() {
      return this.agruparPorNome(this.filtrarPorOrigem(this.linhas));
    },

    embalagensDisponiveis() {
      return this.agruparPorNome(this.filtrarPorOrigem(this.embalagens));
    },

    integracoesDoFabricante() {
      if (!this.filtros.fabricanteId) return this.integracoes;
      return this.integracoes.filter((i) => String(i.fabricanteId) === String(this.filtros.fabricanteId));
    },

    embalagemFormatada() {
      const { embalagemNome, embalagemVolumeMl } = this.editForm;
      if (!embalagemNome && !embalagemVolumeMl) return '';
      const volume = embalagemVolumeMl ? this.formatVolume(embalagemVolumeMl) : '';
      if (embalagemNome && volume) return `${embalagemNome} (${volume})`;
      return embalagemNome || volume;
    },

    baseContextFields() {
      return [
        { label: 'ID',          value: this.editForm.id ? '#' + this.editForm.id : '—', badge: 'secondary', size: 'col-md-3' },
        { label: 'Código',      value: this.editForm.codigo || '—',         badge: 'info',      size: 'col-md-3' },
        { label: 'Linha',       value: this.editForm.linhaNome || '—',                          size: 'col-md-6' },
        { label: 'Embalagem',   value: this.embalagemFormatada || '—',                          size: 'col-md-6' },
        { label: 'Fabricante',  value: this.editForm.nomeFabricante || '—',                     size: 'col-md-6' },
      ];
    },
  },

  methods: {
    // Cada filtro tem o seu parâmetro e todos valem juntos (AND no backend).
    // Antes nome e código dividiam um único `q` — que é um OR entre as colunas
    // —, então um anulava o outro e não dava para cruzar critérios.
    // Uma opção por nome, carregando os ids de todas as origens que a têm.
    agruparPorNome(lista) {
      const porNome = new Map();

      (lista || []).forEach((item) => {
        const atual = porNome.get(item.nome);
        if (atual) {
          atual.ids.push(String(item.id));
          return;
        }
        porNome.set(item.nome, { id: String(item.id), nome: item.nome, ids: [String(item.id)] });
      });

      return Array.from(porNome.values()).map((opcao) => ({
        id: opcao.ids.join(','),
        nome: opcao.nome,
      }));
    },

    descreverFiltro(chave, valor) {
      if (chave === 'fabricanteId') return this.fabricantes[valor] || valor;
      if (chave === 'integracaoId') return this.nomeDaOpcao(this.integracoes, valor);
      if (chave === 'linhaId') return this.nomeDaOpcao(this.linhasDisponiveis, valor);
      if (chave === 'embalagemId') return this.nomeDaOpcao(this.embalagensDisponiveis, valor);
      if (chave === 'eanPreenchido') return valor === '1' ? 'com produto' : 'sem produto';
      return valor;
    },

    filtrarPorOrigem(lista) {
      return (lista || []).filter((item) => {
        const doFabricante = !this.filtros.fabricanteId
          || String(item.fabricanteId) === String(this.filtros.fabricanteId);
        const daOrigem = !this.filtros.integracaoId
          || String(item.integracaoId) === String(this.filtros.integracaoId);
        return doFabricante && daOrigem;
      });
    },

    // O valor do select traz os ids de todas as origens daquela identidade.
    anexarIds(params, chave, valor) {
      if (!valor) return;
      String(valor).split(',').forEach((id) => params.append(`${chave}[]`, id));
    },

    appendFiltros(params) {
      if (this.filtros.fabricanteId) params.append('fabricante_id', this.filtros.fabricanteId);
      this.anexarIds(params, 'linha_id', this.filtros.linhaId);
      this.anexarIds(params, 'embalagem_id', this.filtros.embalagemId);
      if (this.filtros.termo) params.append('q', this.filtros.termo);
      if (this.filtros.nome) params.append('nome', this.filtros.nome);
      if (this.filtros.codigo) params.append('codigo', this.filtros.codigo);
      if (this.filtros.ean) params.append('ean', this.filtros.ean);
      if (this.filtros.eanPreenchido !== '') params.append('ean_preenchido', this.filtros.eanPreenchido);
    },

    openEdit(base) {
      this.editForm = {
        id: base.id,
        codigo: base.codigo,
        linhaNome: base.linhaNome,
        embalagemNome: base.embalagemNome,
        embalagemVolumeMl: base.embalagemVolumeMl,
        nomeFabricante: base.nomeFabricante,
        ean: base.ean,
      };
      this.$modal.show('baseForm');
    },

    formatVolume(ml) {
      return ml >= 1000 ? (ml / 1000).toFixed(1) + 'L' : ml + 'ml';
    },
  },
};
</script>
